import React from 'react';
import { alpha, Button, Grid, IconButton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useTranslation } from 'react-i18next';
import { OrderData } from '../../../api';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { OutlinedIconButton } from '../../../common/ui/OutlinedIconButton';
import { ACTION_SIZE, FONT_SIZE, MUTED_TEXT } from '../agendaStyles';
import { AgendaCardState } from '../utils/occupancyMath';

type AgendaOrderActionsProps = {
  order: OrderData;
  state: AgendaCardState;
  hasExtraItems: boolean;
  amountDue: number;
  canReleaseTable: boolean;
  formatAmount: (amount: number) => React.ReactNode;
  onCheckin: () => void;
  onReleaseTable: () => void;
};

const stop = (event: React.MouseEvent) => event.stopPropagation();

/**
 * The card's actions: beside the rows on desktop, under them on a phone. Progressive by state,
 * never disabled, never boxed. The pencil is always last.
 */
export const AgendaOrderActions = ({
  order,
  state,
  hasExtraItems,
  amountDue,
  canReleaseTable,
  formatAmount,
  onCheckin,
  onReleaseTable,
}: AgendaOrderActionsProps) => {
  const { t } = useTranslation();
  const { openEditOrderDrawer } = useCalendar();
  const isCheckedIn = Boolean(order.checkin);

  return (
    <Grid
      container
      alignItems="center"
      gap={1}
      wrap="nowrap"
      sx={{ width: 'auto', flexShrink: 0, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
      {!isCheckedIn && (
        <Button
          variant="soft"
          onClick={(event) => {
            stop(event);
            onCheckin();
          }}
          sx={{
            minHeight: 44,
            fontSize: FONT_SIZE.text,
            paddingInline: 2,
            whiteSpace: 'nowrap',
            flex: { xs: 1, md: 'none' },
          }}>
          {t('calendar.areas_view.checkin')}
        </Button>
      )}

      {isCheckedIn && (
        <IconButton
          aria-label={t('calendar.areas_view.checked_in')}
          onClick={(event) => {
            stop(event);
            onCheckin();
          }}
          sx={{ width: ACTION_SIZE, height: ACTION_SIZE, color: 'primary.main' }}>
          <CheckIcon />
        </IconButton>
      )}

      {isCheckedIn && (
        <IconButton
          aria-label={t('calendar.edit_order.order_details.extraItems.addItem')}
          onClick={(event) => {
            stop(event);
            openEditOrderDrawer({ order, focusExtraItems: true, openCatalog: !hasExtraItems });
          }}
          sx={{ width: ACTION_SIZE, height: ACTION_SIZE, color: 'text.secondary' }}>
          <ShoppingCartOutlinedIcon />
        </IconButton>
      )}

      {isCheckedIn && hasExtraItems && amountDue > 0 && !order.balance_closed_at && (
        <Button
          variant="text"
          color={state === AgendaCardState.OPEN_BILL ? 'error' : 'primary'}
          onClick={(event) => {
            stop(event);
            openEditOrderDrawer({ order, focusExtraItems: true, openCheckout: true });
          }}
          sx={{
            minHeight: ACTION_SIZE,
            whiteSpace: 'nowrap',
            fontSize: FONT_SIZE.text,
            fontWeight: 700,
            paddingInline: 1.25,
            borderRadius: '6px',
            ...(state === AgendaCardState.OPEN_BILL && {
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
            }),
          }}>
          {t('calendar.areas_view.checkout_amount', { amount: formatAmount(amountDue) })}
        </Button>
      )}

      {canReleaseTable && (
        <IconButton
          aria-label={t('calendar.areas_view.release_table')}
          title={t('calendar.areas_view.release_table')}
          onClick={(event) => {
            stop(event);
            onReleaseTable();
          }}
          sx={{ width: ACTION_SIZE, height: ACTION_SIZE, color: MUTED_TEXT }}>
          <EventAvailableIcon />
        </IconButton>
      )}

      <OutlinedIconButton
        aria-label={t('calendar.areas_view.open_order')}
        onClick={(event) => {
          stop(event);
          openEditOrderDrawer({ order });
        }}
        sx={{ marginInlineStart: { xs: 'auto', md: 0.5 } }}>
        <EditOutlinedIcon />
      </OutlinedIconButton>
    </Grid>
  );
};
